/**
 * This component will be responsible selecting months for the years range
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import "./style.scss";
import CustomModal from "../backdrop-modal";
import { BRANCH_STATUS_MAP, CTA_SIZE, CTA_TYPE  } from "../../../data/constants";
import { Checkbox } from "@mui/material";
import CtaComponent from "../cta";
import useDebounce from "../../../hooks/useDebounce";
import { cloneDeep } from "lodash";
import { REPORTS_SUBTABS_MAP } from "../../../data/reports-constants";

interface Props {
  branchType?: string;
  selectAllBranches?: boolean;
  onApplyCtaClick: any;
  customClass?: string;
  parentClass?: string;
  branchDataList: any;
  modalCustomClass?: string,
  alignmentChangeRequired?: boolean,
  maxSelectionAllowed?: number;
  singleSelection?: boolean;
  initialSelectedBranch?: Record<string, Record<string, string>>
}

const BranchFilterComponent = (props: Props) => {
  const {
    branchType = REPORTS_SUBTABS_MAP.sales.tabId,
    selectAllBranches = false,
    onApplyCtaClick,
    customClass = "",
    parentClass = "",
    branchDataList = [],
    modalCustomClass = "",
    alignmentChangeRequired = false,
    maxSelectionAllowed = 0,
    singleSelection = false,
    initialSelectedBranch = {}
  } = props;
  const [searchValue, setSearchInput] = useState<string>("");
  const [branchesDataMap, setBranchesDataMap] = useState<any>({});
  const [isShowFilterModal, setIsShowFilterModal] = useState<boolean>(false);
  const [activeBranchLength, setActiveBranchLength] = useState<number>(0);
  const [selectedState, setSelectedState] = useState<string>("");
  const [selectedBranchesObj, setSelectedBranchesObj] = useState<any>(singleSelection ? initialSelectedBranch : {}); // {YYYY: {MM: MM_Numric_map}} i.e. {2022: {JAN: "01", "FEB": "02"}}
  const [searchResults, setSearchResults] = useState<any>([]);
  const debouncedSearchTerm: string = useDebounce<string>(searchValue, 500);
  const prevSelection = useRef({});
  const [defaultBranchesSelected, setDefaultBranchesSelected] = useState(false);
  const isBranchTypeSales = branchType === REPORTS_SUBTABS_MAP.sales.tabId;
  const { branchCount = 0, selectedBranch = '' } = useMemo(() => getBranchSelectionDetail(selectedBranchesObj), [selectedBranchesObj])
  const selectionLimitEnabled = maxSelectionAllowed > 0;

  useEffect(() => {
      const data = createBranchDataMap();
      setBranchesDataMap(data);
      setSelectedState(Object.keys(data)[0]);
  }, [branchDataList]);

  useEffect(() => {
    if (
      selectAllBranches &&
      branchesDataMap
    ) {
      setDefaultBranchesSelected(true);
      let defaultBranchesList = cloneDeep(branchDataList) || [];
      defaultBranchesList = defaultBranchesList.filter(
        (branch: any) => branch.status === BRANCH_STATUS_MAP.ACTIVE
      );
      let defaultBranchMap = cloneDeep(branchesDataMap);
      if (
        selectionLimitEnabled &&
        defaultBranchesList?.length > maxSelectionAllowed
      ) {
        defaultBranchesList = defaultBranchesList.slice(0, maxSelectionAllowed);
        defaultBranchMap = createBranchDataMap(defaultBranchesList);
      }
      setSelectedBranchesObj(defaultBranchMap);
      prevSelection.current = defaultBranchMap;
      const all_selected_branches: any = [];
      for (let state in defaultBranchMap) {
        //iterating over the map which has months corresponding to their years
        all_selected_branches.push(...Object.keys(branchesDataMap[state]));
      }
      onApplyCtaClick(all_selected_branches);
    }
  }, [branchesDataMap]);

  // Whenever the debounce term change it will be triggered
  useEffect(
    () => {
      if (debouncedSearchTerm) {
        setSearchResults(getSearchResults());
      } else {
        setSearchResults([]);
      }
    },
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );

  /**
   * This function will return the search results based upon the value entered in search box
   */
  function getSearchResults() {
    const data = cloneDeep(branchDataList);
    let results = data.filter((branch: any) => {
      if (branch?.status === BRANCH_STATUS_MAP.ACTIVE) {
        return branch.branchName
          ?.toLocaleLowerCase()
          ?.includes(searchValue.toLocaleLowerCase());
      }
    });
    return results;
  }
  // A inventory branch have branch label test or inventory
  const isInventoryBranch = (branch: any) => {
    let isInventoryBranch;
    branch?.branchLabels?.forEach((value: string) => {
      if (value?.toLowerCase() === 'test' || value?.toLowerCase() === 'inventory') {
        isInventoryBranch = true;
      }
    })
    return !!isInventoryBranch;
  }

  /**
   * This function will parse over the permittedBranchesData from reducer and make a map where all the branches corresponding to a
   * city will be present
   */
  function createBranchDataMap(branchList: any[] = branchDataList) {
    let newBranchData: any = {};
    //to store count of active branches
    let activeBranches = 0;
    branchList?.forEach((branch: any) => {
      // Checking for inventory branch
      if (isInventoryBranch(branch)) {
        return;
      }
      else {
        if (branch?.status === BRANCH_STATUS_MAP.ACTIVE) {
          const state = branch?.address?.state,
            branchId = isBranchTypeSales ? branch.branchId : branch.mid;
          activeBranches++;
          if (newBranchData[state]) newBranchData[state][branchId] = branch;
          else {
            newBranchData[state] = {};
            newBranchData[state][branchId] = branch;
          }
        }
      }
    });
    setActiveBranchLength(activeBranches);
    return newBranchData;
  }

  /**
   * This function will be triggered when user clicks on branch. It will update the selectedBranchesObj and add/remove the selected branch
   * to their corresponding state in a map
   * @param item : a object which has branchName and branchID
   * @param isSelected : it identifies whether user has select/deselct a month
   * @param branchState: it identifies the state of the branch and by default it pick up from state else from argument
   */
  const onBranchSelection = (
    item: any,
    isSelected: boolean = false,
    branchState = ""
  ) => {
    const brid = isBranchTypeSales ? item.branchId : item.mid;
    let branchSelectionMap = cloneDeep(selectedBranchesObj);
    if (branchState) setSelectedState(branchState);
    else branchState = selectedState;

    if(singleSelection)
    {
      setSelectedBranchesObj({});
      branchSelectionMap = {};
      if(!isSelected)
      {
      branchSelectionMap[branchState] = {};
      branchSelectionMap[branchState][brid] = item.branchName;
      }
    }
    else {  
      if (branchSelectionMap[branchState]) {
        if (isSelected) delete branchSelectionMap[branchState][brid];
        //deleting the year if the month is unselected
        else branchSelectionMap[branchState][brid] = item.branchName;
      } else {
        branchSelectionMap[branchState] = {};
        branchSelectionMap[branchState][brid] = item.branchName;
      }
    }
    let totalSelectionCount = getTotalSelectionCount(branchSelectionMap);
    if (
      !selectionLimitEnabled ||
      totalSelectionCount <= maxSelectionAllowed
    ) {
      setSelectedBranchesObj(branchSelectionMap);
    }
  };

  /**
   * This function will be triggered when user clicks on the select all checkbox of a particular branch
   * @param isChecked
   */
  const onSelectAllClick = (state: string, isChecked: boolean) => {
    let branchSelectionMap = cloneDeep(selectedBranchesObj);
    if (!isChecked) {
      branchSelectionMap[state] = cloneDeep(
        branchesDataMap[state]
      );
    } else {
      delete branchSelectionMap[state];
    }
    let totalSelectionCount = getTotalSelectionCount(branchSelectionMap);
    if (
      !selectionLimitEnabled ||
      totalSelectionCount <= maxSelectionAllowed
    ) {
      setSelectedBranchesObj(branchSelectionMap);
    }
  };

  /**
   * This function will return a boolean value to check if all the branches of a state has been selected or not
   */
  const areAllBranchesOfStateSelected = (state: string): boolean => {
    return selectedBranchesObj?.[state] &&
      branchesDataMap?.[state] &&
      Object.keys(selectedBranchesObj?.[state])?.length ===
      Object.keys(branchesDataMap?.[state])?.length
      ? true
      : false;
  };

  /**
   * This function will be triggered when user clicks on apply button and it will call the parent function and pass all the
   * selected branchesID of the corresponding state in an array
   */
  const onApplyBtnClick = () => {
    const all_selected_branches: any = [];
    for (let state in selectedBranchesObj) {
      //iterating over the map which has months corresponding to their years
      all_selected_branches.push(...Object.keys(selectedBranchesObj[state]));
    }
    onApplyCtaClick(all_selected_branches);
    prevSelection.current = cloneDeep(selectedBranchesObj);
    setIsShowFilterModal(false);
    setSearchInput("");
  };

  const resetData = () => {
    setSelectedBranchesObj({});
    onApplyCtaClick([]);
    prevSelection.current = {};
    setSearchInput("");
  };

  const getTotalSelectionCount = (
    selectedBranchesData: any = selectedBranchesObj
  ) => {
    let count = 0;
    for (let key in selectedBranchesData) {
      count += Object.values(selectedBranchesData[key]).length || 0;
    }
    return count;
  };

  function getBranchSelectionDetail(selectedBranchData: any = {}) {
    let branchCount = 0;
    let selectedBranch = ''
    Object.keys(selectedBranchData)?.forEach((key) => {
      const selectedBranchKeys = Object.keys(selectedBranchData[key] || {})
      if (selectedBranchKeys.length) {
        branchCount++;
        selectedBranch = selectedBranchData[key][selectedBranchKeys[0]]
      }
    })
    return {
      branchCount,
      selectedBranch
    }
  }

  function renderActionBox(): JSX.Element {
    return (
      <section className="px-16 py-12 flex justify-between items-center absolute bottom-0 bg-white w-full border-t-[1px] border-[#EFEFEF]">
        <div>
          <p className="text-xs font-normal text-[#969696] mb-8">
            Total {getBranchType(true)} Selected
          </p>
          <p className="text-base text-black text-[0.935rem] opacity-80 tracking-wider">
            <span className="font-semibold">{getTotalSelectionCount()}</span>/{activeBranchLength}
          </p>
        </div>
        <CtaComponent
          ctaSize={CTA_SIZE.small}
          ctaType={CTA_TYPE.secondary_active_cta}
          onCtaClick={onApplyBtnClick}
          ctaText={"Confirm"}
          isDisabled={!getTotalSelectionCount()}
        ></CtaComponent>
      </section>
    );
  }

  function renderBranchesByState(): JSX.Element {
    return (
      <section className={`w-full max-h-[100%] overflow-auto px-[16px]`}>
        {Object.keys(branchesDataMap[selectedState])?.map(
          (branchId: string) => {
            const isSelected = selectedBranchesObj[selectedState]?.[branchId]
              ? true
              : false;
            return (
              <div
                key={branchId}
                className={`flex items-center my-20 cursor-pointer text-14 sm:text-[14px] capitalize
                         ${isSelected ? "font-semibold" : "font-regular"}`}
                onClick={() => {
                    onBranchSelection(
                      branchesDataMap[selectedState][branchId],
                      isSelected ? true : false
                    )
                }
                }
              >
                <Checkbox
                  size={"small"}
                  checked={isSelected}
                  inputProps={{ "aria-label": "controlled" }}
                  className="!p-0"
                />
                <div className="ml-8 font-normal">
                  {branchesDataMap[selectedState][branchId].branchName}
                </div>
              </div>
            );
          }
        )}
      </section>
    );
  }

  function renderStates(): JSX.Element {
    return (
      <section className="border-r-[1px] border-[#EFEFEF] max-h-[100%] overflow-auto shrink-0">
        {Object.keys(branchesDataMap).map((item: string) => {
          const isSelected = selectedState === item;
          const isAllSelected = areAllBranchesOfStateSelected(item)

          return (
            <div
              key={item}
              className={`w-full cursor-pointer py-16 flex flex-row justify-between items-center year-box px-12 border-l-[4px] hover:bg-[#F8F8F8] break-words 
              ${
                isSelected
                  ? "bg-[#f2f8ff] border-[#0074FB]"
                  : "bg-white border-transparent"
              }`}
              onClick={() => setSelectedState(item)}
            >
              <div className="flex items-center mr-12">
                {!singleSelection && <Checkbox
                  size={"small"}
                  onChange={(e, checked) => onSelectAllClick(item, !checked)}
                  checked={isAllSelected}
                  inputProps={{ "aria-label": "controlled" }}
                  className="!p-0"
                />}
                <p className={`text-14 text-sm ${isSelected ? "font-semibold" : ""} ml-8 sm:text-[14px]`}>{item}</p>
              </div>
              <span style={{borderRadius: 8}} className="px-[6px] py-2 text-xs text-[#767676] bg-[#f4f4f4]">
                <span className="font-semibold text-[#636363]">{Object.keys(selectedBranchesObj?.[item] ?? {})?.length}</span>/{Object.keys(branchesDataMap?.[item] ?? {})?.length}
              </span>
            </div>
          );
        })}
      </section>
    );
  }

  /**
   * This function will render the search results
   */
  function renderSearchResults(): JSX.Element {
    return (
      <section
        className={`flex flex-col max-h-[100%] px-16 ${
          selectionLimitEnabled ? "pt-[126px]" : "pt-70"
        } pb-70`}
      >
        {searchResults.length ? (
          <>
            <p className="text-14 font-normal opacity-50">
              {searchResults.length} Result found
            </p>
            <div className="overflow-auto">
              {searchResults.map((item: any) => {
                const brid = isBranchTypeSales ? item.branchId : item.mid;
                const isSelected =
                  !!selectedBranchesObj[item.address.state]?.[brid];
                return (
                  <div
                    key={brid}
                    className={`flex items-center my-20 cursor-pointer text-14 capitalize
                          ${isSelected ? "font-semibold" : "font-regular"}`}
                    onClick={() =>
                      onBranchSelection(item, isSelected, item.address.state)
                    }
                  >
                    <Checkbox
                      size={"small"}
                      checked={isSelected}
                      inputProps={{ "aria-label": "controlled" }}
                      className="!p-0"
                    />
                    <div className="ml-8">{item.branchName}</div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <p className="text-14 font-normal opacity-50">No results found</p>
        )}
      </section>
    );
  }

  const isAllBranchesSelected = (): boolean => {
    return getTotalSelectionCount() === activeBranchLength;
  };

  function renderAllBranchesSelection(): JSX.Element {
    const isSelected = isAllBranchesSelected();
    return (
      <div
        onClick={() => {
          if ((!selectionLimitEnabled ||
            activeBranchLength <= maxSelectionAllowed)
          ) {
            setSelectedBranchesObj(
              isSelected ? {} : cloneDeep(branchesDataMap)
            );
          }
        }}
        className={`flex items-center pt-12 px-16 mt-16 border-[#EFEFEF] border-t-[1px] select-all-branch-option text-14 
         justify-between font-medium`}
      >
        <div
          className="flex items-center cursor-pointer w-full select-none"
        >
          <Checkbox
            size={"small"}
            checked={isSelected}
            inputProps={{ "aria-label": "controlled" }}
            className="!p-0"
          />
          <p className="font-medium text-[0.938rem] ml-8">Select All {getBranchType(true)}</p>
        </div>
      </div>
    );
  }

  function renderSelectionInfo() {
    return (
      <div className="w-[cacl(100%+32px)] py-[8px] bg-black/5 px-16 -mx-16 mt-8">
        <div className="text-[12px] text-black/50">
          You can only select {maxSelectionAllowed} {getBranchType(true)} at a time
        </div>
        <div className="text-15 flex items-center justify-between">
          <span className="text-black/70 font-medium">{getBranchType(true)} Selected</span>
          <span className="text-black font-semibold">
            {getTotalSelectionCount()}/{maxSelectionAllowed}
          </span>
        </div>
      </div>
    );
  }

  function toggleModal() {
    //disabled if not data present
    if(Object.keys(branchesDataMap || {})?.length)
      setIsShowFilterModal(true);
  }

  const getBranchType = (pleural = false) => {
    return isBranchTypeSales ? `Branch${pleural ? "es" : ""}` : `Store${pleural ? "s" : ""}`;
  }

  return (
    <div
      className={`branch-filter-component relative w-[190px] h-[36px] ${parentClass || ""
        } sm:w-[100%] ml-16 sm:ml-[0px] sm:mt-12`}
      onClick={toggleModal}
    >
      <div
        className={`placeholder-box cursor-pointer whitespace-nowrap w-full flex items-center h-[36px] hover:border-black border-[#D8D8D8] rounded-[5px] border-[1px] pl-16 pr-30 text-15 ${customClass || ""
          } ${!getTotalSelectionCount() && "text-[#A1A2A3]"}`}
      >
        {!singleSelection ? getTotalSelectionCount()
          ? `${getTotalSelectionCount()} ${getBranchType(getTotalSelectionCount() > 1)} Selected`
          : getBranchType(true) : (selectedBranch?.length ? selectedBranch : 'Select branches')}
        <div
          className={`absolute right-[12px] top-[calc(50%-8px)] rista-sprite ${
            isShowFilterModal
              ? "ic-arrow-up-grey-sm"
              : "ic-arrow-down-grey-sm"
          }`}
        ></div>
      </div>
      {isShowFilterModal && (
        <CustomModal
          open={isShowFilterModal}
          onClose={() => {
            setSelectedBranchesObj(prevSelection.current);
            setIsShowFilterModal(false);
            setSearchInput("");
          }}
          customClass={`border-[#F0F0F0] border-[1px] w-[400px] sm:w-[100%] shadow-[0px_2px_10px_#00000014] p-[0px] md:h-[60vh] md:max-h-[440px] sm:h-[440px] pt-8 z-2000 ${modalCustomClass} ${alignmentChangeRequired ? "alignment-class" : ""
            }`}
        >
          <div
            className={`search-branch-wrap absolute top-0 bg-white w-full p-16 border-[#EFEFEF] border-b-[1px] ${
              selectionLimitEnabled ? "pb-0" : "pb-12"
            }`}
          >
            <input
              type="text"
              onChange={(e) =>
                setSearchInput(e.target.value)
              }
              className="search-input-box text-13 bg-[#FAFAFA] font-regular pr-16 pl-[40px] focus:pl-16 py-8 rounded-[6px] border-[1px] border-[#EFEFEF] w-[100%] focus-visible:border-[#000000] sm:text-[14px]"
              placeholder={
                `Search by ${getBranchType()} Name`
              }
              value={searchValue}
            ></input>
            <div className="rista-sprite ic-search absolute top-[22px] left-[26px] scale-[0.83]"></div>
            {selectionLimitEnabled
              ? renderSelectionInfo()
              : searchValue.length < 3 && renderAllBranchesSelection()}
          </div>
          {searchValue.length >= 3 ? (
            renderSearchResults()
          ) : (
            <section
              className={`flex max-h-[100%] h-[100%] ${
                selectionLimitEnabled ? "pt-[110px]" : "pt-[105px]"
              } pb-70`}
            >
              {renderStates()}
              {renderBranchesByState()}
            </section>
          )}
          {renderActionBox()}
        </CustomModal>
      )}
    </div>
  );
};

export default BranchFilterComponent;
